/**
 * Analytics views for the checkout dashboard, kept out of the main campaigns
 * screen so that screen stays a working list rather than a report.
 *
 *   CampaignTrend  — KPI tiles + a hover-readable visitors/leads/purchases
 *                    trend. Rendered inside a campaign's own Analytics view.
 *   DayMatrix      — day-column heat grid. Rows are either the checkout pages
 *                    (all-campaigns view) or the metrics (single campaign).
 *   DayWiseTable   — the day-end report: one row per IST day, expandable into
 *                    the pages that made it up.
 *
 * All three read the SAME /checkout-analytics-daily payload, so no two views
 * can ever disagree about a number.
 *
 * The chart is hand-drawn SVG on purpose: this app ships no chart library on
 * this route, and a trend this size does not justify adding one.
 */
import React, { useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import {
  DAY_METRICS,
  day,
  dayConvPct,
  fmt,
  pctChange,
  relativeDayLabel,
  sumMetrics,
  weekday,
} from "./analyticsUtils";

const G = "#04b85c";
const G_SOFT = "#e9f9f0";
const INK = "#0e1f18";
const MUTED = "#7b8a83";
const LINE = "#e6ebe8";
const UP = "#0f7a3d";
const DOWN = "#c0392b";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * Change against the comparison period, shown beside a value.
 *
 * Always carries BOTH the absolute move and the rate: "+14" alone hides scale,
 * "+70%" alone hides that it was 20 → 34. The tooltip names the two periods
 * outright, because the previous complaint about this screen was not knowing
 * what a percentage was measured against.
 */
export const Delta = ({ current, previous, label, compact }) => {
  if (previous == null || current == null) return null;
  const diff = current - previous;
  if (diff === 0 && !previous) return null;
  const p = pctChange(current, previous);
  const up = diff > 0;
  const flat = diff === 0;
  const Icon = up ? ArrowDropUpIcon : ArrowDropDownIcon;
  const color = flat ? "#aab6b0" : up ? UP : DOWN;
  return (
    <Tooltip
      title={
        label ||
        `${fmt(current)} vs ${fmt(previous)} previously — ${
          up ? "up" : flat ? "no change of" : "down"
        } ${fmt(Math.abs(diff))}${p === null ? "" : ` (${up ? "+" : ""}${p}%)`}`
      }
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          fontSize: compact ? 10.5 : 11.5,
          fontWeight: 700,
          color,
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          cursor: "help",
        }}
      >
        {flat ? (
          "no change"
        ) : (
          <>
            <Icon style={{ fontSize: 15, margin: "0 -3px" }} />
            {up ? "+" : "−"}
            {fmt(Math.abs(diff))}
            {p !== null && (
              <span style={{ color: MUTED, fontWeight: 600, marginLeft: 3 }}>
                {up ? "+" : ""}
                {p}%
              </span>
            )}
          </>
        )}
      </span>
    </Tooltip>
  );
};

/* ------------------------------------------------------------------ *
 * CampaignTrend — KPI tiles + hoverable trend chart for ONE campaign
 * ------------------------------------------------------------------ */
export const CampaignTrend = ({ days, name, classes, hideTiles }) => {
  // `days` arrives newest-first; charts read left-to-right in time order.
  const series = useMemo(() => days.slice().reverse(), [days]);
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  // Split the window in half so the tiles compare like with like instead of
  // against a single (possibly freak) day.
  const half = Math.floor(days.length / 2);
  const totals = useMemo(() => sumMetrics(days), [days]);
  const recent = useMemo(() => sumMetrics(days.slice(0, half || 1)), [days, half]);
  const older = useMemo(
    () => (half ? sumMetrics(days.slice(half, half * 2)) : null),
    [days, half]
  );

  const W = 1000;
  const H = 240;
  const PADL = 44;
  const PADR = 14;
  const PADB = 28;
  const PADT = 14;
  const max = Math.max(
    1,
    ...series.map((d) => Math.max(d.uniqueVisitors, d.leads, d.purchases))
  );
  // A floor keeps the axis from degenerating to "1 1 1 0 0" on a quiet range.
  const top = Math.max(max * 1.12, 4);
  const x = (i) =>
    series.length === 1 ? W / 2 : PADL + i * ((W - PADL - PADR) / (series.length - 1));
  const y = (v) => PADT + (H - PADT - PADB) * (1 - v / top);
  const path = (key) => series.map((p, i) => `${x(i)},${y(p[key])}`).join(" ");

  const LINES = [
    { key: "uniqueVisitors", color: "#b9d9c8", width: 2, label: "Visitors" },
    { key: "leads", color: G, width: 2.6, label: "Leads" },
    { key: "purchases", color: "#1565c0", width: 2.6, label: "Purchases" },
  ];

  /** Nearest point to the cursor, in viewBox space. */
  const onMove = (e) => {
    if (!svgRef.current || !series.length) return;
    const r = svgRef.current.getBoundingClientRect();
    const vx = ((e.clientX - r.left) / r.width) * W;
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < series.length; i++) {
      const d = Math.abs(x(i) - vx);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    setHover(best);
  };

  if (!days.length) {
    return (
      <Box style={{ padding: 40, textAlign: "center", color: MUTED }}>
        No activity for {name} in this range.
      </Box>
    );
  }

  const hp = hover == null ? null : series[hover];
  const empty =
    !totals.leads && !totals.purchases && !totals.callbacks && !totals.uniqueVisitors;

  return (
    <Box style={{ padding: "4px 4px 8px" }}>
      {/* The Combined group view already shows these totals in the page ribbon,
          so its trend suppresses the duplicate tiles. */}
      {!hideTiles && (
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {DAY_METRICS.map((m, i) => (
          <Box
            key={m.key}
            className={classes && classes.pop}
            style={{
              border: `1px solid ${LINE}`,
              borderRadius: 12,
              padding: "12px 14px",
              background: "#fbfdfc",
              // 60ms apart: enough to read as a sequence, short enough that
              // the whole row is settled before the eye reaches the chart.
              animationDelay: `${i * 60}ms`,
            }}
          >
            <div
              style={{
                fontSize: 11.5,
                color: MUTED,
                fontWeight: 650,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {m.label}
            </div>
            <div
              style={{
                fontSize: 26,
                fontWeight: 750,
                color: INK,
                lineHeight: 1.15,
                marginTop: 3,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmt(totals[m.key])}
            </div>
            {older && (
              <div style={{ marginTop: 2 }}>
                <Delta
                  current={recent[m.key]}
                  previous={older[m.key]}
                  label={`Latest ${half} days: ${fmt(recent[m.key])} — previous ${half} days: ${fmt(
                    older[m.key]
                  )}`}
                />
                <span
                  style={{ marginLeft: 5, fontSize: 11, fontWeight: 600, color: MUTED }}
                >
                  last {half}d vs prior {half}d
                </span>
              </div>
            )}
          </Box>
        ))}
      </Box>
      )}

      <Box
        className={classes && classes.pop}
        style={{
          border: `1px solid ${LINE}`,
          borderRadius: 12,
          background: "#fff",
          position: "relative",
          animationDelay: "190ms",
        }}
      >
        <Box
          style={{
            display: "flex",
            gap: 16,
            padding: "12px 14px 6px",
            fontSize: 12,
            color: MUTED,
          }}
        >
          {LINES.map((l) => (
            <span key={l.key}>
              <i
                style={{
                  display: "inline-block",
                  width: 9,
                  height: 9,
                  borderRadius: 3,
                  background: l.color,
                  marginRight: 6,
                }}
              />
              {l.label}
            </span>
          ))}
          <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 11 }}>
            {series.length} days · IST · hover for a day
          </span>
        </Box>

        <style>{`@keyframes sparkDraw {
          from { stroke-dashoffset: 12000 }
          to   { stroke-dashoffset: 0 }
        }`}</style>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          role="img"
          style={{ display: "block", cursor: "crosshair" }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((f) => {
            const yy = PADT + (H - PADT - PADB) * f;
            return (
              <g key={f}>
                <line x1={PADL} x2={W - PADR} y1={yy} y2={yy} stroke="#eef2f0" />
                <text x={8} y={yy + 4} fontSize="10.5" fill={MUTED}>
                  {Math.round(top * (1 - f))}
                </text>
              </g>
            );
          })}

          {/* Guide line first, so the series draw over it. */}
          {hp && (
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PADT}
              y2={H - PADB}
              stroke={G}
              strokeWidth="1"
              strokeDasharray="3 3"
            />
          )}

          {/* Each line draws left-to-right by animating its own dash offset —
              a stroke length far above any real path length, so one keyframe
              range works for every series without measuring the path. */}
          {LINES.map((l, i) => (
            <polyline
              key={l.key}
              fill="none"
              stroke={l.color}
              strokeWidth={l.width}
              strokeLinejoin="round"
              strokeLinecap="round"
              points={path(l.key)}
              style={{
                strokeDasharray: 12000,
                animation: `sparkDraw 1.05s cubic-bezier(.25,.8,.3,1) ${
                  240 + i * 110
                }ms both`,
              }}
            />
          ))}

          {/* Dots only on the hovered day; drawing every point on a 300-day
              series turns the chart into a smear. */}
          {hp &&
            LINES.map((l) => (
              <circle
                key={l.key}
                cx={x(hover)}
                cy={y(hp[l.key])}
                r="4.5"
                fill="#fff"
                stroke={l.color}
                strokeWidth="2.5"
              />
            ))}

          {series.map((p, i) =>
            series.length <= 10 || i % Math.ceil(series.length / 8) === 0 ? (
              <text
                key={`l-${p.day}`}
                x={x(i)}
                y={H - 8}
                fontSize="10.5"
                fill={MUTED}
                textAnchor="middle"
              >
                {day(p.day).slice(0, 5)}
              </text>
            ) : null
          )}
        </svg>

        {empty && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: MUTED,
              fontSize: 13.5,
              fontWeight: 600,
              pointerEvents: "none",
            }}
          >
            No activity for {name} in this range.
          </Box>
        )}

        {/* HTML tooltip rather than SVG <title>: <title> needs a hover pause
            and cannot be styled, which is why hovering felt broken before. */}
        {hp && (
          <Box
            style={{
              position: "absolute",
              // Flip to the left of the guide once past the midpoint so the
              // card never runs off the panel.
              left: `${(x(hover) / W) * 100}%`,
              transform:
                x(hover) > W * 0.6 ? "translate(-108%, 0)" : "translate(8%, 0)",
              top: 52,
              pointerEvents: "none",
              background: "#0e1f18",
              color: "#fff",
              borderRadius: 10,
              padding: "9px 12px",
              boxShadow: "0 8px 22px rgba(0,0,0,.22)",
              minWidth: 168,
              zIndex: 3,
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 750, marginBottom: 5 }}>
              {day(hp.day)}{" "}
              <span style={{ color: "#9fd9bb", fontWeight: 600 }}>
                {weekday(hp.day)}
              </span>
            </div>
            {DAY_METRICS.map((m) => (
              <div
                key={m.key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 16,
                  fontSize: 12,
                  lineHeight: 1.6,
                }}
              >
                <span style={{ color: "#b9c9c1" }}>{m.label}</span>
                <b style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(hp[m.key])}</b>
              </div>
            ))}
            <div
              style={{
                borderTop: "1px solid #24493a",
                marginTop: 6,
                paddingTop: 5,
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                fontSize: 12,
              }}
            >
              <span style={{ color: "#b9c9c1" }}>Lead → paid</span>
              <b>{dayConvPct(hp)}</b>
            </div>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * MonthCalendar — one calendar month, every day of it, shaded by volume
 *
 * A grid of dates is how people actually hold a month in their head: the
 * weekend columns line up, a dead week is a visible band, and a spike sits
 * on a date you can name. A day with no data and a day with a zero are
 * drawn differently — an empty cell must never read as "we got nothing".
 * ------------------------------------------------------------------ */
const METRIC_OPTIONS = [
  { key: "leads", label: "Leads" },
  { key: "purchases", label: "Purchases" },
  { key: "uniqueVisitors", label: "Visitors" },
  { key: "callbacks", label: "Callbacks" },
];

const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const MonthCalendar = ({ days, istTodayKey }) => {
  const [metric, setMetric] = useState("leads");

  const byDay = useMemo(() => {
    const m = new Map();
    for (const d of days) m.set(d.day, d);
    return m;
  }, [days]);

  // Months that the loaded range actually covers, newest first.
  const months = useMemo(() => {
    if (!days.length) return [];
    const set = new Set(days.map((d) => d.day.slice(0, 7)));
    return [...set].sort().reverse();
  }, [days]);

  // Land on the most recent month that has something in it, not on an empty
  // current month — otherwise the first thing a founder sees is a blank grid.
  const defaultMonth = useMemo(() => {
    const live = days.find((d) => d.leads || d.purchases || d.callbacks);
    return (live || days[0] || {}).day
      ? (live || days[0]).day.slice(0, 7)
      : null;
  }, [days]);

  const [monthKey, setMonthKey] = useState(null);
  const month = monthKey && months.includes(monthKey) ? monthKey : defaultMonth;
  const idx = months.indexOf(month);

  const cells = useMemo(() => {
    if (!month) return [];
    const first = moment(`${month}-01`, "YYYY-MM-DD");
    const out = [];
    // Blank leaders so the 1st sits under its real weekday.
    for (let i = 1; i < first.isoWeekday(); i++) out.push(null);
    const len = first.daysInMonth();
    for (let d = 1; d <= len; d++) {
      const key = first.clone().date(d).format("YYYY-MM-DD");
      out.push({ key, date: d, row: byDay.get(key) || null });
    }
    return out;
  }, [month, byDay]);

  const monthTotals = useMemo(
    () => sumMetrics(cells.filter((c) => c && c.row).map((c) => c.row)),
    [cells]
  );

  // Scale within the month: a busy month and a quiet one should each be
  // readable, rather than the quiet one rendering uniformly blank.
  const peak = useMemo(
    () =>
      Math.max(
        // Floor of 8: without it a month whose best day was 1 lead paints
        // that day the same full green as a 47-lead day in a busy month, and
        // the grid reads as a great month at a glance.
        8,
        ...cells.filter((c) => c && c.row).map((c) => c.row[metric] || 0)
      ),
    [cells, metric]
  );

  if (!month) {
    return (
      <Box style={{ padding: 40, textAlign: "center", color: MUTED }}>
        No activity in this range.
      </Box>
    );
  }

  const shade = (v) => {
    if (!v) return { bg: "#fbfdfc", fg: "#c2ccc7" };
    const t = v / peak;
    if (t < 0.25) return { bg: "#eafaf1", fg: INK };
    if (t < 0.5) return { bg: "#c6efd9", fg: INK };
    if (t < 0.75) return { bg: "#7fd6a5", fg: "#08351f" };
    return { bg: G, fg: "#fff" };
  };

  const navBtn = (dir, label, disabled) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setMonthKey(months[idx + dir])}
      style={{
        border: `1px solid ${LINE}`,
        background: disabled ? "#f6f8f7" : "#fff",
        color: disabled ? "#c2ccc7" : INK,
        borderRadius: 8,
        width: 30,
        height: 30,
        cursor: disabled ? "default" : "pointer",
        fontSize: 15,
        lineHeight: 1,
        fontFamily: "inherit",
      }}
      aria-label={label}
    >
      {dir > 0 ? "\u2039" : "\u203a"}
    </button>
  );

  return (
    <Box>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 10,
          maxWidth: 620,
          margin: "0 auto 10px",
        }}
      >
        {/* months is newest-first, so "previous month" is a HIGHER index. */}
        {navBtn(1, "Previous month", idx >= months.length - 1)}
        <span style={{ fontWeight: 750, color: INK, fontSize: 15, minWidth: 132 }}>
          {moment(`${month}-01`, "YYYY-MM-DD").format("MMMM YYYY")}
        </span>
        {navBtn(-1, "Next month", idx <= 0)}

        <Box style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
          {METRIC_OPTIONS.map((m) => (
            <span
              key={m.key}
              onClick={() => setMetric(m.key)}
              style={{
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 11px",
                borderRadius: 20,
                border: `1px solid ${metric === m.key ? "#9fdcbb" : LINE}`,
                background: metric === m.key ? G_SOFT : "#f4f7f5",
                color: metric === m.key ? UP : MUTED,
                transition: "all .16s ease",
              }}
            >
              {m.label}
            </span>
          ))}
        </Box>
      </Box>

      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 4,
          // A calendar reads as a calendar at calendar proportions; stretched
          // across a full-width panel the cells become banners.
          maxWidth: 620,
          margin: "0 auto",
        }}
      >
        {WEEK.map((w) => (
          <div
            key={w}
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: MUTED,
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: 0.3,
              paddingBottom: 2,
            }}
          >
            {w}
          </div>
        ))}

        {cells.map((c, i) => {
          if (!c) return <div key={`pad-${i}`} />;
          // No row at all = outside the loaded range. Drawn as a hole, not
          // as a zero, so an unloaded day is never read as a dead day.
          const missing = !c.row;
          const v = missing ? null : c.row[metric] || 0;
          const s = missing ? { bg: "#fff", fg: "#dbe3df" } : shade(v);
          const isToday = c.key === istTodayKey;
          return (
            <Tooltip
              key={c.key}
              title={
                missing ? (
                  `${day(c.key)} — outside the selected range`
                ) : (
                  <span style={{ fontSize: 12, lineHeight: 1.7 }}>
                    <b>{day(c.key)}</b> ({weekday(c.key)})
                    <br />
                    {DAY_METRICS.map((m) => (
                      <span key={m.key}>
                        {m.label}: <b>{fmt(c.row[m.key])}</b>
                        <br />
                      </span>
                    ))}
                    Lead → paid: <b>{dayConvPct(c.row)}</b>
                  </span>
                )
              }
            >
              <div
                style={{
                  border: isToday ? `2px solid ${G}` : `1px solid ${LINE}`,
                  borderRadius: 8,
                  background: s.bg,
                  color: s.fg,
                  aspectRatio: "1 / 1",
                  padding: "4px 6px 5px",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "default",
                  transition: "transform .12s ease, box-shadow .12s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(14,31,24,.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    opacity: missing ? 1 : 0.66,
                    lineHeight: 1,
                  }}
                >
                  {c.date}
                </span>
                <span
                  style={{
                    marginTop: "auto",
                    textAlign: "right",
                    fontSize: 14,
                    fontWeight: 750,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                  }}
                >
                  {missing ? "" : v || "·"}
                </span>
              </div>
            </Tooltip>
          );
        })}
      </Box>

      <Box
        style={{
          display: "flex",
          gap: 22,
          flexWrap: "wrap",
          marginTop: 12,
          paddingTop: 10,
          maxWidth: 620,
          margin: "12px auto 0",
          justifyContent: "center",
          borderTop: `1px solid ${LINE}`,
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 750, color: INK }}>
          {moment(`${month}-01`, "YYYY-MM-DD").format("MMMM")} total
        </span>
        {DAY_METRICS.map((m) => (
          <span key={m.key} style={{ fontSize: 12.5, color: MUTED }}>
            {m.label}{" "}
            <b style={{ color: INK, fontVariantNumeric: "tabular-nums" }}>
              {fmt(monthTotals[m.key])}
            </b>
          </span>
        ))}
        <span style={{ fontSize: 12.5, color: MUTED }}>
          Lead → paid <b style={{ color: INK }}>{dayConvPct(monthTotals)}</b>
        </span>
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * DayWiseTable — the day-end report
 * ------------------------------------------------------------------ */

/** Days with literally nothing on them, collapsed into one line. */
const isQuiet = (d) =>
  !d.leads && !d.purchases && !d.callbacks && !d.uniqueVisitors;

/**
 * Runs of ≥2 consecutive dead days become a single foldable row. Over a
 * long range those rows are most of the table, and a founder scrolling past
 * twenty rows of zeroes never reaches the days that mattered.
 */
const groupQuietRuns = (days) => {
  const out = [];
  let run = null;
  days.forEach((d, i) => {
    if (isQuiet(d)) {
      run = run || { type: "quiet", items: [] };
      run.items.push({ d, i });
      return;
    }
    if (run) {
      out.push(run.items.length > 1 ? run : { type: "day", ...run.items[0] });
      run = null;
    }
    out.push({ type: "day", d, i });
  });
  if (run) out.push(run.items.length > 1 ? run : { type: "day", ...run.items[0] });
  return out;
};

export const DayWiseTable = ({ days, classes, openDay, setOpenDay }) => {
  const totals = useMemo(() => sumMetrics(days), [days]);
  const groups = useMemo(() => groupQuietRuns(days), [days]);
  const [openQuiet, setOpenQuiet] = useState({});

  // Data bar scale: busiest day in view, so the bars read as "share of the
  // best day" rather than an arbitrary absolute.
  const peak = useMemo(
    () => Math.max(1, ...days.map((d) => d.leads || 0)),
    [days]
  );

  if (!days.length) {
    return (
      <Box style={{ padding: 40, textAlign: "center", color: MUTED }}>
        No activity in this range.
      </Box>
    );
  }

  const DayRow = ({ d, i }) => {
    // Newest-first over a gap-filled series, so the next entry is genuinely
    // the calendar day before — that is what makes the delta trustworthy.
    const prev = days[i + 1];
    const expandable = Array.isArray(d.campaigns);
    const isOpen = expandable && openDay === d.day;
    const rel = relativeDayLabel(d.day);
    return (
      <React.Fragment>
        <TableRow
          hover={expandable}
          className={expandable ? classes.clickRow : undefined}
          onClick={() => expandable && setOpenDay(isOpen ? null : d.day)}
        >
          <TableCell className={classes.cell}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {expandable && (
                <ExpandMoreIcon
                  style={{
                    fontSize: 16,
                    color: MUTED,
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform .18s ease",
                  }}
                />
              )}
              <span style={{ fontWeight: 650, color: INK, fontSize: 13.5 }}>
                {day(d.day)}
              </span>
              <span style={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>
                {weekday(d.day)}
              </span>
              {rel && (
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: UP,
                    background: G_SOFT,
                    borderRadius: 6,
                    padding: "1px 6px",
                  }}
                >
                  {rel}
                </span>
              )}
            </span>
          </TableCell>
          {DAY_METRICS.map((m) => {
            const pct = m.key === "leads" ? ((d.leads || 0) / peak) * 100 : 0;
            return (
              <TableCell
                key={m.key}
                className={`${classes.cell} ${classes.num}`}
                align="right"
              >
                <div style={{ fontWeight: 650, color: INK, fontSize: 13.5 }}>
                  {fmt(d[m.key])}
                </div>
                {prev && (
                  <div style={{ marginTop: 1 }}>
                    <Delta
                      compact
                      current={d[m.key]}
                      previous={prev[m.key]}
                      label={`${m.label} — ${day(d.day)}: ${fmt(d[m.key])}, previous day ${day(
                        prev.day
                      )}: ${fmt(prev[m.key])}`}
                    />
                  </div>
                )}
                {/* Leads carry a bar so the shape of the period is readable
                    by scanning, without reading a single number. */}
                {m.key === "leads" && (
                  <div
                    style={{
                      height: 3,
                      borderRadius: 2,
                      marginTop: 4,
                      marginLeft: "auto",
                      width: `${Math.max(pct, d.leads ? 4 : 0)}%`,
                      minWidth: d.leads ? 4 : 0,
                      background: G,
                      opacity: 0.55,
                    }}
                  />
                )}
              </TableCell>
            );
          })}
          <TableCell className={`${classes.cell} ${classes.num}`} align="right">
            <span style={{ fontWeight: 650, color: INK }}>{dayConvPct(d)}</span>
          </TableCell>
        </TableRow>

        {isOpen &&
          (d.campaigns || []).map((c) => (
            <TableRow key={`${d.day}-${c.region}-${c.version}`}>
              <TableCell
                className={classes.cell}
                style={{ paddingLeft: 34, background: "#fbfdfc" }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, color: INK }}>
                  {c.subSource || `${c.region}-${c.version}`}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>
                  {c.region}/{c.version}
                </div>
              </TableCell>
              {DAY_METRICS.map((m) => (
                <TableCell
                  key={m.key}
                  className={`${classes.cell} ${classes.num}`}
                  align="right"
                  style={{ background: "#fbfdfc", color: MUTED }}
                >
                  {fmt(c[m.key])}
                </TableCell>
              ))}
              <TableCell
                className={`${classes.cell} ${classes.num}`}
                align="right"
                style={{ background: "#fbfdfc", color: MUTED }}
              >
                {dayConvPct(c)}
              </TableCell>
            </TableRow>
          ))}
        {isOpen && !(d.campaigns || []).length && (
          <TableRow>
            <TableCell
              className={classes.cell}
              colSpan={DAY_METRICS.length + 2}
              style={{ paddingLeft: 34, background: "#fbfdfc", color: MUTED }}
            >
              No checkout page recorded any activity on this day.
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  return (
    // All time is 400+ rows. Without a pinned header a reader scrolling to
    // March has no idea which column is which.
    <TableContainer style={{ overflow: "auto", maxHeight: days.length > 14 ? 640 : "none" }}>
      <Table size="small" stickyHeader={days.length > 14}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headCell}>Date</TableCell>
            {DAY_METRICS.map((m) => (
              <TableCell key={m.key} className={classes.headCell} align="right">
                <div>{m.label}</div>
                <div style={{ fontWeight: 500, fontSize: 10, color: MUTED }}>
                  vs previous day
                </div>
              </TableCell>
            ))}
            <TableCell className={classes.headCell} align="right">
              <div>Lead → paid</div>
              <div style={{ fontWeight: 500, fontSize: 10, color: MUTED }}>
                same day
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Totals sit at the TOP: the first thing a founder wants is the
              period number, not the bottom of a 300-row scroll. */}
          {days.length > 1 && (
            <TableRow>
              <TableCell
                className={classes.cell}
                style={{ fontWeight: 750, color: INK, background: "#f4f8f6" }}
              >
                Total · {days.length} days
              </TableCell>
              {DAY_METRICS.map((m) => (
                <TableCell
                  key={m.key}
                  className={`${classes.cell} ${classes.num}`}
                  align="right"
                  style={{ fontWeight: 750, color: INK, background: "#f4f8f6" }}
                >
                  {fmt(totals[m.key])}
                  {m.key === "uniqueVisitors" && (
                    <Tooltip title="Visitors are counted unique per day. Someone returning on several days counts once per day, so this total is not a period-unique count.">
                      <InfoOutlinedIcon
                        style={{
                          fontSize: 13,
                          color: MUTED,
                          marginLeft: 4,
                          verticalAlign: -2,
                        }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
              ))}
              <TableCell
                className={`${classes.cell} ${classes.num}`}
                align="right"
                style={{ fontWeight: 750, color: INK, background: "#f4f8f6" }}
              >
                {dayConvPct(totals)}
              </TableCell>
            </TableRow>
          )}

          {groups.map((g) =>
            g.type === "day" ? (
              <DayRow key={g.d.day} d={g.d} i={g.i} />
            ) : openQuiet[g.items[0].d.day] ? (
              <React.Fragment key={`q-${g.items[0].d.day}`}>
                <TableRow>
                  <TableCell
                    className={classes.cell}
                    colSpan={DAY_METRICS.length + 2}
                    style={{ background: "#fafcfb", color: MUTED, cursor: "pointer" }}
                    onClick={() =>
                      setOpenQuiet((s) => ({ ...s, [g.items[0].d.day]: false }))
                    }
                  >
                    Hide {g.items.length} days with no activity
                  </TableCell>
                </TableRow>
                {g.items.map((it) => (
                  <DayRow key={it.d.day} d={it.d} i={it.i} />
                ))}
              </React.Fragment>
            ) : (
              <TableRow
                key={`q-${g.items[0].d.day}`}
                hover
                className={classes.clickRow}
                onClick={() =>
                  setOpenQuiet((s) => ({ ...s, [g.items[0].d.day]: true }))
                }
              >
                <TableCell
                  className={classes.cell}
                  colSpan={DAY_METRICS.length + 2}
                  style={{ background: "#fafcfb" }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: MUTED,
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    <ExpandMoreIcon style={{ fontSize: 16 }} />
                    {g.items.length} days with no activity ·{" "}
                    {day(g.items[g.items.length - 1].d.day)} → {day(g.items[0].d.day)}
                  </span>
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
